<template>
  <!-- Shared branded toaster for the donation widget. Positioned
       fixed above everything (z-index 10000) so notifications never
       render behind the stepper or the modal chrome. Custom template
       renders a white card with a colored left accent bar, inline
       icon, title + message stack, and a close X.

       `max="3"` caps the simultaneous stack so a noisy source (e.g.
       repeated Stripe validation errors) can never build an
       uncloseable pile — oldest toasts roll out as new ones arrive. -->
  <!-- animation-type defaults to "css" — we explicitly pick the CSS
       path and name a transition class. The "velocity" animation type
       requires the velocity-animate library to be registered at
       plugin install time (`app.use(Notifications, { velocity })`),
       which this project doesn't do; when it's missing, the library's
       leave hook calls `this.velocity()` as undefined and throws.
       The thrown error aborts the leave animation, so the notification
       node never actually unmounts — the close X appears to do
       nothing. CSS animation has no such dependency. -->
  <notifications
    position="top center"
    group="donationWidget"
    :duration="4500"
    :speed="320"
    :width="340"
    :max="3"
    :ignore-duplicates="true"
    animation-type="css"
    animation-name="widget-notif-anim"
    class="widget-notifications"
  >
    <template #body="{ item, close }">
      <div class="widget-notif" :class="'widget-notif--' + (item.type || 'info')" role="status">
        <span class="widget-notif__accent" aria-hidden="true" />
        <span class="widget-notif__icon" aria-hidden="true" v-html="iconFor(item.type)" />
        <div class="widget-notif__body">
          <p v-if="item.title" class="widget-notif__title">{{ item.title }}</p>
          <p class="widget-notif__text">{{ item.text }}</p>
        </div>
        <button class="widget-notif__close" type="button" aria-label="Dismiss" @click="close">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </template>
  </notifications>
</template>

<script>
// Lucide-derived glyphs inlined so the component carries no icon-lib
// dependency. One per notification type; info is the default.
const ICONS = {
  error:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>'
    + '<line x1="12" y1="9" x2="12" y2="13"/>'
    + '<line x1="12" y1="17" x2="12.01" y2="17"/>'
    + '</svg>',
  warn:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + '<circle cx="12" cy="12" r="10"/>'
    + '<line x1="12" y1="8" x2="12" y2="12"/>'
    + '<line x1="12" y1="16" x2="12.01" y2="16"/>'
    + '</svg>',
  success:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">'
    + '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>'
    + '<polyline points="22 4 12 14.01 9 11.01"/>'
    + '</svg>',
  info:
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">'
    + '<circle cx="12" cy="12" r="10"/>'
    + '<line x1="12" y1="16" x2="12" y2="12"/>'
    + '<line x1="12" y1="8" x2="12.01" y2="8"/>'
    + '</svg>',
}

export default {
  name: 'WidgetNotifications',
  methods: {
    iconFor(type) {
      return ICONS[type] || ICONS.info
    },
  },
}
</script>

<style>
/* ─── Container ────────────────────────────────────────────────
   Overrides the library's inline `position: fixed` to `absolute`
   so the toaster anchors to the form column (nearest `relative`
   ancestor) — viewport-top-centered notifications felt
   disconnected from the widget on desktop where the modal is
   vertically centered. Library still sets left: 50% + margin-left
   to center, which now centers within the form column → visually
   top-center of the widget panel's right half.

   z-10000 sits above the stepper (z-5), the modal chrome (z-40),
   and the tribute swing (z-30). Because the enclosing form column
   has no z-index, this competes in the modal panel's stacking
   context → always on top. */
.widget-notifications {
  position: absolute !important;
  z-index: 10000 !important;
}

/* CSS transition for enter/leave. Named after the animation-name
   prop above. Slide-fade from top with a spring-like cubic-bezier
   for entrance, a quicker decelerate for exit. */
.widget-notif-anim-enter-active,
.widget-notif-anim-leave-active {
  transition:
    opacity 260ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
.widget-notif-anim-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}
.widget-notif-anim-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
.widget-notif-anim-move { transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1); }

/* Reset the library's default notification wrapper styles so our
   custom template is the only visible chrome. */
.widget-notifications .vue-notification-wrapper {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
  margin: 0 0 10px !important;
  overflow: visible !important;
}

/* ─── Branded card ────────────────────────────────────────── */
.widget-notif {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px 12px 18px;
  background: #ffffff;
  border: 1px solid rgba(29, 37, 73, 0.08);
  border-radius: 12px;
  box-shadow:
    0 16px 32px -16px rgba(218, 24, 132, 0.25),
    0 8px 20px -10px rgba(10, 13, 28, 0.25),
    0 0 0 1px rgba(29, 37, 73, 0.02);
  font-family: 'Red Hat Display', system-ui, sans-serif;
  color: #111827;
  min-width: 280px;
  max-width: 340px;
  overflow: hidden;
}

/* Left accent bar — color encodes notification type while the
   card stays on-brand white. */
.widget-notif__accent {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 4px;
  border-radius: 12px 0 0 12px;
  background: var(--widget-notif-accent, #DA1884);
}
.widget-notif--error   { --widget-notif-accent: #EF4444; }
.widget-notif--warn    { --widget-notif-accent: #F59E0B; }
.widget-notif--success { --widget-notif-accent: #16A34A; }
.widget-notif--info    { --widget-notif-accent: #DA1884; }

/* Icon chip matches the accent color on a tinted background. */
.widget-notif__icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: var(--widget-notif-accent, #DA1884);
  background: color-mix(in srgb, var(--widget-notif-accent, #DA1884) 10%, transparent);
}
/* Fallback for browsers without color-mix() — use per-type tint. */
@supports not (background: color-mix(in srgb, red, blue)) {
  .widget-notif--error   .widget-notif__icon { background: rgba(239, 68, 68, 0.12); }
  .widget-notif--warn    .widget-notif__icon { background: rgba(245, 158, 11, 0.14); }
  .widget-notif--success .widget-notif__icon { background: rgba(22, 163, 74, 0.12); }
  .widget-notif--info    .widget-notif__icon { background: rgba(218, 24, 132, 0.12); }
}
.widget-notif__icon svg { width: 18px; height: 18px; stroke: currentColor; }

/* Body copy. Title is optional; text is always present. */
.widget-notif__body {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.widget-notif__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1D2549;
  letter-spacing: -0.005em;
  line-height: 1.2;
}
.widget-notif__text {
  margin: 2px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #4B5563;
  word-wrap: break-word;
}
.widget-notif__title + .widget-notif__text { margin-top: 4px; }

/* Close X — frosted circle that tints on hover. */
.widget-notif__close {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 9999px;
  color: #9CA3AF;
  cursor: pointer;
  transition:
    background-color 160ms ease,
    color 160ms ease,
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
}
.widget-notif__close:hover {
  background: rgba(29, 37, 73, 0.06);
  color: #111827;
  transform: rotate(90deg);
}
.widget-notif__close:focus-visible {
  outline: 2px solid var(--widget-notif-accent, #DA1884);
  outline-offset: 2px;
}
</style>
